import {
  Component,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { gsap } from 'gsap';
import { Observable, filter, map, tap } from 'rxjs';
import { ThreadWithMessages } from 'src/app/core/services/communication.service';
import { UserService } from '../../../../../core/services/user.service';
import { GuideService } from '../../../pages/guide/services/guide.service';
import { ClaimsService } from '../../../../../core/services/claims.service';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
})
export class SideNavComponent implements OnInit {
  @Input()
  public notifications$?: Observable<ThreadWithMessages[]>;

  @ViewChild('drawer', { static: false })
  drawer!: MatDrawer;
  isToShow = false;
  isGuide = false;
  navLocked = false;

  mobile = false;

  constructor(
    private router: Router,
    private us: UserService,
    private guideService: GuideService,
    private claimsService: ClaimsService
  ) {}

  ngOnInit(): void {
    this.isMobile();
    this.checkIfGuide();
    this.checkNavLock();

    this.router.events
      .pipe(
        filter((ev) => ev instanceof NavigationEnd),
        tap(() => {
          this.checkIfGuide();
          this.checkNavLock();
        })
      )
      .subscribe();

    const savedState = localStorage.getItem('sidenavOpened');
    if (savedState !== null && !this.isMobile() && !this.isGuide) {
      this.isToShow = savedState === 'true';
      this.isToShow && this.animateLinks();
    }
  }

  checkIfGuide() {
    this.isGuide = this.router.url.includes('/guide');
    // On mobile, while the questionnaire wizard ("wizard-mode") is active we hide the
    // sandwich menu to keep the user locked into the funnel. If the drawer happened to be
    // open when the wizard begins, close it so nothing remains accessible.
    if (this.isGuide && this.isMobile() && this.isToShow) {
      this.isToShow = false;
    }
  }

  /**
   * ✅ CPK-4172: enquanto o login veio de uma landing page (flag setado pelo
   * OneTimeLinkComponent) e nenhum claim do usuário tem legal pack assinado,
   * trava a navegação do menu e do avatar de perfil. Libera assim que
   * qualquer claim tiver legal pack assinado (e remove o flag, permanente).
   */
  async checkNavLock(): Promise<void> {
    const cameFromLp = localStorage.getItem('cpCameFromLp') === 'true';
    if (!cameFromLp) {
      this.navLocked = false;
      return;
    }

    const hasSignedLegalPack = await this.claimsService.hasAnyLegalPackSigned();
    if (hasSignedLegalPack) {
      localStorage.removeItem('cpCameFromLp');
      this.navLocked = false;
      return;
    }

    this.navLocked = true;
  }

  isMobile(): boolean {
    this.mobile = window.innerWidth < 990;
    return this.mobile;
  }

  closeSideNav() {
    if (this.isMobile()) this.isToShow = false;
  }

  toggleSidenav(forceValue?: boolean) {
    // if (this.isGuide) return;
    this.isToShow = forceValue ?? !this.isToShow;
    localStorage.setItem('sidenavOpened', this.isToShow.toString());
    this.animateLinks();
  }

  closedStart() {
    this.toggleSidenav(false);
  }

  animateLinks() {
    const links = document.querySelectorAll('.animatable-menu-span');
    if (this.isToShow) {
      gsap.fromTo(
        links,
        { opacity: 0, x: -10 },
        { opacity: 1, x: 0, duration: 0.5, stagger: 0.1 }
      );
    } else {
      gsap.to(links, { opacity: 0, duration: 0.2 });
    }
  }

  logout() {
    localStorage.removeItem('userRole');
    this.guideService.resetService();
    this.us.signOut();
  }

  @HostListener('window:resize')
  onResize() {
    this.isMobile();
    if (this.isToShow) {
      this.toggleSidenav();
    }
  }
}
